/**
 * PartitionMappingStep - Wizard step 3 for mapping directory levels to dataset partitions
 *
 * This step requires users to:
 * - View discovered subdirectory levels in the monitored directory
 * - Map each directory level to a partition column defined in the target dataset
 * - All directories at the same level represent values of the same partition
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Loader2,
  AlertCircle,
  CheckCircle,
  FolderOpen,
  RefreshCw,
  ArrowRight,
  Info,
  Layers,
} from 'lucide-react';
import {
  useMonitorCreationStore,
  validateLevelPartitionMappings,
} from '@/features/monitors/store/monitorCreationStore';
import { useDataset } from '@/hooks/useDatasets';
import { useListSubdirectories } from '@/features/monitors/hooks/useMonitors';
import type { SubdirectoryInfo, LevelPartitionMapping } from '@/features/monitors/types';

/**
 * Represents a directory level with sample directories at that level
 */
interface DirectoryLevel {
  level: number;
  directories: SubdirectoryInfo[];
  sampleNames: string[];
  totalCount: number;
}

/**
 * PartitionMappingStep Component
 *
 * Third step of monitor creation wizard for mapping directory levels to dataset partitions.
 * Groups directories by level and allows mapping each level to a partition column.
 *
 * @example
 * ```tsx
 * <PartitionMappingStep />
 * ```
 */
export const PartitionMappingStep: React.FC = () => {
  // Use individual selectors to avoid creating new objects on every render
  const directory_path = useMonitorCreationStore((state) => state.directory_path);
  const directoryValidated = useMonitorCreationStore((state) => state.directoryValidated);
  const dataset_id = useMonitorCreationStore((state) => state.dataset_id);
  const level_partition_mappings = useMonitorCreationStore((state) => state.level_partition_mappings);
  const setLevelPartitionMappings = useMonitorCreationStore((state) => state.setLevelPartitionMappings);
  const updateLevelPartitionMapping = useMonitorCreationStore((state) => state.updateLevelPartitionMapping);
  const setPartitionMappingsValidated = useMonitorCreationStore((state) => state.setPartitionMappingsValidated);

  // Fetch dataset details to get partition columns
  const { data: dataset, isLoading: datasetLoading, error: datasetError } = useDataset(
    dataset_id
  );

  // Fetch subdirectories from the monitored directory
  const listSubdirectoriesMutation = useListSubdirectories();
  const listSubdirectoriesRef = useRef(listSubdirectoriesMutation.mutateAsync);
  useEffect(() => {
    listSubdirectoriesRef.current = listSubdirectoriesMutation.mutateAsync;
  });

  const [subdirectories, setSubdirectories] = useState<SubdirectoryInfo[]>([]);
  const [subdirLoading, setSubdirLoading] = useState(false);
  const [subdirError, setSubdirError] = useState<string | null>(null);
  const [hasLoadedSubdirs, setHasLoadedSubdirs] = useState(false);

  // Store setLevelPartitionMappings in a ref to avoid dependency changes
  const setLevelPartitionMappingsRef = useRef(setLevelPartitionMappings);
  useEffect(() => {
    setLevelPartitionMappingsRef.current = setLevelPartitionMappings;
  });

  // Extract partition columns from dataset
  const datasetPartitions: string[] = useMemo(() => {
    if (!dataset) return [];
    return dataset.partitions || [];
  }, [dataset]);

  // Track the depth we last loaded with to detect when we need to reload
  const lastLoadedDepthRef = useRef<number>(0);

  // Group subdirectories by level
  const directoryLevels: DirectoryLevel[] = useMemo(() => {
    if (subdirectories.length === 0) return [];

    const levelMap = new Map<number, SubdirectoryInfo[]>();

    // Group directories by level
    for (const subdir of subdirectories) {
      const level = subdir.level || 1;
      if (!levelMap.has(level)) {
        levelMap.set(level, []);
      }
      levelMap.get(level)!.push(subdir);
    }

    // Convert to array sorted by level
    const levels: DirectoryLevel[] = [];
    const sortedLevels = Array.from(levelMap.keys()).sort((a, b) => a - b);

    for (const level of sortedLevels) {
      const dirs = levelMap.get(level)!;
      // Get up to 5 sample names for display
      const sampleNames = dirs.slice(0, 5).map((d) => d.name);
      levels.push({
        level,
        directories: dirs,
        sampleNames,
        totalCount: dirs.length,
      });
    }

    return levels;
  }, [subdirectories]);

  // Maximum level found in directories
  const maxLevel = useMemo(() => {
    return directoryLevels.length > 0
      ? Math.max(...directoryLevels.map((l) => l.level))
      : 0;
  }, [directoryLevels]);

  // Load subdirectories when directory path is available
  useEffect(() => {
    const depth = datasetPartitions.length || 1;

    if (!directory_path || !directoryValidated) return;
    if (hasLoadedSubdirs && lastLoadedDepthRef.current >= depth) return;

    const loadSubdirectories = async () => {
      setSubdirLoading(true);
      setSubdirError(null);

      try {
        const result = await listSubdirectoriesRef.current({ path: directory_path, depth });
        setSubdirectories(result.subdirectories || []);
        setHasLoadedSubdirs(true);
        lastLoadedDepthRef.current = depth;

        // Initialize level partition mappings based on discovered levels
        if (result.subdirectories && result.subdirectories.length > 0) {
          // Group by level to find unique levels
          const levelSet = new Set<number>();
          const levelSamples = new Map<number, string[]>();

          for (const subdir of result.subdirectories) {
            const level = subdir.level || 1;
            levelSet.add(level);
            if (!levelSamples.has(level)) {
              levelSamples.set(level, []);
            }
            const samples = levelSamples.get(level)!;
            if (samples.length < 5) {
              samples.push(subdir.name);
            }
          }

          // Create level mappings with auto-assignment based on level order
          const sortedLevels = Array.from(levelSet).sort((a, b) => a - b);
          const newMappings: LevelPartitionMapping[] = sortedLevels.map((level, index) => ({
            level,
            partition_name: datasetPartitions[index] || '',
            sample_values: levelSamples.get(level) || [],
          }));

          setLevelPartitionMappingsRef.current(newMappings);
        }
      } catch (error: any) {
        console.error('Failed to load subdirectories:', error);
        setSubdirError(error.response?.data?.detail || error.message || 'Failed to load subdirectories');
      } finally {
        setSubdirLoading(false);
      }
    };

    loadSubdirectories();
  }, [directory_path, directoryValidated, hasLoadedSubdirs, datasetPartitions]);

  // Validate mappings when they change
  const validationErrors = useMemo(() => {
    return validateLevelPartitionMappings(level_partition_mappings, datasetPartitions, maxLevel);
  }, [level_partition_mappings, datasetPartitions, maxLevel]);

  const isValid = Object.keys(validationErrors).length === 0 && datasetPartitions.length > 0;

  // Update validated state when validation changes
  useEffect(() => {
    setPartitionMappingsValidated(isValid);
  }, [isValid, setPartitionMappingsValidated]);

  /**
   * Handle partition selection for a level
   */
  const handlePartitionSelect = (level: number, partitionName: string) => {
    const actualValue = partitionName === '__none__' ? '' : partitionName;
    updateLevelPartitionMapping(level, actualValue);
  };

  /**
   * Refresh subdirectories list
   */
  const handleRefresh = useCallback(async () => {
    if (!directory_path) return;

    setSubdirLoading(true);
    setSubdirError(null);

    try {
      const depth = datasetPartitions.length || 1;
      const result = await listSubdirectoriesRef.current({ path: directory_path, depth });
      setSubdirectories(result.subdirectories || []);
    } catch (error: any) {
      setSubdirError(error.response?.data?.detail || 'Failed to refresh subdirectories');
    } finally {
      setSubdirLoading(false);
    }
  }, [directory_path, datasetPartitions]);

  /**
   * Get available partitions (not already assigned to another level)
   */
  const getAvailablePartitions = (currentLevel: number): string[] => {
    const usedPartitions = new Set(
      level_partition_mappings
        .filter((m) => m.level !== currentLevel && m.partition_name)
        .map((m) => m.partition_name)
    );

    return datasetPartitions
      .filter((p) => !usedPartitions.has(p))
      .sort((a, b) => a.localeCompare(b));
  };

  /**
   * Get the current partition assigned to a level
   */
  const getCurrentPartition = (level: number): string => {
    const mapping = level_partition_mappings.find((m) => m.level === level);
    return mapping?.partition_name || '__none__';
  };

  // Loading states
  if (datasetLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-3 text-muted-foreground">Loading dataset information...</span>
      </div>
    );
  }

  // Error states
  if (datasetError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load dataset information: {datasetError.message || 'Unknown error'}
        </AlertDescription>
      </Alert>
    );
  }

  // No partitions in dataset
  if (datasetPartitions.length === 0) {
    return (
      <div className="space-y-6">
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            <strong>No partitions defined in dataset</strong>
            <p className="mt-2">
              The selected dataset "{dataset?.name}" has no partition columns defined.
              Partition mapping is only required when the dataset has partitions.
            </p>
            <p className="mt-2 text-muted-foreground">
              You can proceed to the next step, or go back and select a different dataset
              that has partitions configured.
            </p>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5" />
            Directory Level Mapping
          </CardTitle>
          <CardDescription>
            Map directory levels in <code className="bg-muted px-1 py-0.5 rounded">{directory_path}</code> to
            partition columns. All directories at the same level represent values of the same partition.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Dataset Partitions Info */}
          <div className="rounded-lg border bg-muted/50 p-4">
            <h4 className="font-medium mb-2">Dataset Partitions ({datasetPartitions.length})</h4>
            <div className="flex flex-wrap gap-2">
              {datasetPartitions.map((partition) => {
                const isMapped = level_partition_mappings.some(
                  (m) => m.partition_name === partition
                );
                return (
                  <span
                    key={partition}
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                      isMapped
                        ? 'bg-success/10 text-success'
                        : 'bg-warning/10 text-warning'
                    }`}
                  >
                    {isMapped && <CheckCircle className="h-3 w-3 mr-1" />}
                    {partition}
                  </span>
                );
              })}
            </div>
          </div>

          {/* Refresh Button */}
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={subdirLoading}
            >
              {subdirLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Refresh Subdirectories
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Subdirectory Loading */}
      {subdirLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">Scanning subdirectories...</span>
        </div>
      )}

      {/* Subdirectory Error */}
      {subdirError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{subdirError}</AlertDescription>
        </Alert>
      )}

      {/* No Subdirectories */}
      {!subdirLoading && !subdirError && subdirectories.length === 0 && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            No subdirectories found in the monitored directory. The monitor will process
            files directly in the root directory.
          </AlertDescription>
        </Alert>
      )}

      {/* Level-based Mapping List */}
      {!subdirLoading && directoryLevels.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Directory Level Mappings ({directoryLevels.length} level{directoryLevels.length !== 1 ? 's' : ''})
            </CardTitle>
            <CardDescription>
              Each directory level maps to one partition column. All directories at a level
              are treated as values of that partition.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {directoryLevels.map((levelInfo) => {
                const currentPartition = getCurrentPartition(levelInfo.level);
                const availablePartitions = getAvailablePartitions(levelInfo.level);
                const hasActualPartition = currentPartition && currentPartition !== '__none__';
                const selectOptions = hasActualPartition
                  ? [currentPartition, ...availablePartitions.filter((p) => p !== currentPartition)]
                  : availablePartitions;

                return (
                  <div
                    key={levelInfo.level}
                    className="p-4 rounded-lg border bg-card"
                  >
                    <div className="flex items-start gap-4">
                      {/* Level Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold text-sm">
                            {levelInfo.level}
                          </span>
                          <span className="font-medium">Level {levelInfo.level}</span>
                          <span className="text-sm text-muted-foreground">
                            ({levelInfo.totalCount} director{levelInfo.totalCount !== 1 ? 'ies' : 'y'})
                          </span>
                        </div>

                        {/* Sample directories */}
                        <div className="ml-10 space-y-1">
                          <p className="text-sm text-muted-foreground mb-1">Sample directories:</p>
                          <div className="flex flex-wrap gap-2">
                            {levelInfo.sampleNames.map((name, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center px-2 py-1 rounded bg-muted text-sm font-mono"
                              >
                                <FolderOpen className="h-3 w-3 mr-1 text-muted-foreground" />
                                {name}
                              </span>
                            ))}
                            {levelInfo.totalCount > 5 && (
                              <span className="text-sm text-muted-foreground">
                                ...and {levelInfo.totalCount - 5} more
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Arrow */}
                      <ArrowRight className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-2" />

                      {/* Partition Selector */}
                      <div className="w-48 flex-shrink-0">
                        <Select
                          value={currentPartition}
                          onValueChange={(value) => handlePartitionSelect(levelInfo.level, value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select partition..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">-- Not mapped --</SelectItem>
                            {selectOptions.map((partition) => (
                              <SelectItem key={partition} value={partition}>
                                {partition}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Validation Errors */}
      {Object.keys(validationErrors).length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Mapping incomplete:</strong>
            <ul className="list-disc list-inside mt-2">
              {Object.values(validationErrors).map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Success Message */}
      {isValid && level_partition_mappings.length > 0 && (
        <Alert variant="success">
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>All partitions mapped!</strong>
            <p className="mt-1">
              {level_partition_mappings.filter((m) => m.partition_name).length} directory level
              {level_partition_mappings.filter((m) => m.partition_name).length === 1 ? '' : 's'} mapped
              to {datasetPartitions.length} partition
              {datasetPartitions.length === 1 ? '' : 's'}.
            </p>
          </AlertDescription>
        </Alert>
      )}

      {/* Help Info */}
      <Alert variant="info">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription className="space-y-2 text-sm">
          <p className="font-medium">About Level-based Partition Mapping</p>
          <p>
            Level-based mapping treats all directories at the same depth as values of the same partition.
          </p>
          <ul className="list-disc list-inside space-y-1">
            <li>Level 1 directories are the first level under the monitored directory</li>
            <li>All directories at a level (e.g., 2024-11-10, 2024-11-11) map to the same partition (e.g., date)</li>
            <li>Directory names at each level will be used as partition values during ingestion</li>
            <li>Each partition column should be mapped to exactly one directory level</li>
          </ul>
          <div className="mt-3 p-3 bg-muted rounded">
            <p className="font-medium mb-1">Example:</p>
            <p className="font-mono text-xs">
              /data/linkedin-ads/<br />
              ├── 2025-11-10/  (Level 1 → date partition)<br />
              │   ├── account-123/  (Level 2 → account_id partition)<br />
              │   └── account-456/<br />
              └── 2025-11-11/<br />
              &nbsp;&nbsp;&nbsp;&nbsp;└── account-789/
            </p>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
};
